import { useContext } from 'react';
import { MobXProviderContext } from 'mobx-react';
import { useCompanyInfo } from '../';
import {
    ICompaniesHashSettingList,
    ICompaniesHierarchyAPI,
    ICompaniesHierarchyTable,
    ICompaniesList,
    IFilterParams,
    ISelectList,
    IStatItem,
} from '../../interfaces';
import { getCurrencyShortCodeOrCodeById, mapStatApiItems, stringCount, stringMoney } from '../../instruments';
import { IShowMoneyItem, showMoneyList } from '../../Components';

export function useCompaniesTable(): ICompaniesList {
    return useContext(MobXProviderContext).RootStore.companiesStore.list;
}

export function useCompaniesSelect(): ISelectList {
    return useContext(MobXProviderContext).RootStore.companiesStore.select;
}

export function useCompaniesHashSettingsList(): ICompaniesHashSettingList {
    return useContext(MobXProviderContext).RootStore.companiesStore.companiesHashSettingsList;
}

export function useCompanyTypesSelect(): ISelectList {
    return useContext(MobXProviderContext).RootStore.companiesStore.companyTypeSelect;
}

export function usePaymentTypesSelect(): ISelectList {
    return useContext(MobXProviderContext).RootStore.companiesStore.paymentTypeSelect;
}

export function useCompaniesChildrenSelect(): ISelectList {
    return useContext(MobXProviderContext).RootStore.companiesStore.childrenSelect;
}

export function useMyCompanySelect(): ISelectList {
    const { parent = '', parentId = '' } = useCompanyInfo();

    return [{ id: parentId, name: parent, disabled: false }];
}

// ------------ HierarchyTableList ------------

type IUseCompaniesHierarchyIsHaveChildren = {
    findChildren(parentId: string): boolean;
};

export function useCompaniesHierarchyIsHaveChildren(): IUseCompaniesHierarchyIsHaveChildren {
    const companiesHierarchyList = useContext(MobXProviderContext).RootStore.companiesStore.companiesHierarchyList;
    const findChildren = (parentId: string): boolean => {
        return !!companiesHierarchyList.find((c: ICompaniesHierarchyAPI) => c.parentId === parentId);
    };

    return {
        findChildren,
    };
}

const treeCompaniesHierarchyTable = (list: Array<ICompaniesHierarchyTable>): Array<ICompaniesHierarchyTable> => {
    return list
        .reduce((a: Array<ICompaniesHierarchyTable>, c: ICompaniesHierarchyTable) => {
            c.children = list.filter((i: ICompaniesHierarchyTable) => i.parentId === c.key);
            a.push(c);
            return a;
        }, [])
        .filter((i) => !i.parentId);
};

export function useCompaniesHierarchyTable(): Array<ICompaniesHierarchyTable> {
    const companiesHierarchyList = useContext(MobXProviderContext).RootStore.companiesStore.companiesHierarchyList;
    const lineList: Array<ICompaniesHierarchyTable> = companiesHierarchyList.map((c: ICompaniesHierarchyAPI) => {
        const currencySymbol =
            c.currency?.currency_symbol || c.currency?.currency_abbreviation || c.currency?.currency_id
                ? getCurrencyShortCodeOrCodeById(c.currency.currency_id ? c.currency.currency_id.toString() : '')
                : '';
        const stats = mapStatApiItems(c.costs_and_revenues);
        const item: ICompaniesHierarchyTable = {
            balance: stringMoney(c.balance, currencySymbol),
            cardsCount: stringCount(c.cards_count),
            childrenCount: c.children_count || 0,
            key: c.id,
            name: c.name || '',
            parentId: c.parentId,
            paymentType: c.payment_type?.name || '',
            costList: showMoneyList(
                stats.map((i: IStatItem): IShowMoneyItem => ({ value: i.cost, symbol: i.currencySymbol })),
            ),
            revenueList: showMoneyList(
                stats.map((i: IStatItem): IShowMoneyItem => ({ value: i.revenue, symbol: i.currencySymbol })),
            ),
        };
        return item;
    });
    return treeCompaniesHierarchyTable(lineList);
}

export function useCompaniesFilterParams(): IFilterParams {
    return useContext(MobXProviderContext).RootStore.companiesStore.filterParams;
}
